import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { ConfirmationOfApplication } from '@/features/ui';
import { useProceedNextFromConfirmation } from '@/features/ui/Steps/ConfirmationOfApplication/utils/useProceedNextFromConfirmation';
import { DeliveryAddressPreview } from '@/features/ui/Steps/DeliveryAddress/DeliveryAddress';

import { ConditionView, Link, Loader, StepControlPanel, Typography } from '@/shared/ui';

import { ExtractFormType } from '@/modules/iac/pages/services/ExtendedCriminalRecord/lib/constants';
import { ExtendedCriminalRecordExtractSchema } from '@/modules/iac/pages/services/ExtendedCriminalRecord/model/types';
import { BirthPlaceAndRegistrationPreview } from '@/modules/iac/pages/services/ExtendedCriminalRecord/ui/steps/BirthPlaceAndRegistrationStep';
import { ExtractFormTypePreview } from '@/modules/iac/pages/services/ExtendedCriminalRecord/ui/steps/ExtractFormTypeStep';
import { PersonalDataPreview } from '@/modules/iac/pages/services/ExtendedCriminalRecord/ui/steps/PersonalDataStep';
import { PurposeAndExtractTypePreview } from '@/modules/iac/pages/services/ExtendedCriminalRecord/ui/steps/PurposeAndExtractTypeStep';

import { ROUTES } from '@/router/baseRoutes';

import { FormDataType } from '@/api/types';

export interface DataPreviewProps {
  handleGeneratePDF: (formData: FormDataType) => Promise<void>;
}

export const DataPreviewStep: React.FC<StepProps<DataPreviewProps>> = ({ next, prev, handleGeneratePDF }) => {
  const { watch, getValues } = useFormContext<
    ExtendedCriminalRecordExtractSchema & {
      confirmEnteredDataCorrect: boolean;
    }
  >();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { proceedNextFromConfirmation } = useProceedNextFromConfirmation(next);

  const confirmEnteredDataCorrect = watch('confirmEnteredDataCorrect');

  const handleNextStep = async (): Promise<void> => {
    setIsLoading(true);

    handleGeneratePDF(getValues())
      .then(proceedNextFromConfirmation)
      .catch(console.error)
      .finally(() => setIsLoading(false));
  };

  return (
    <ConditionView if={!isLoading} else={<Loader />}>
      <FormBlock title="Підтвердження даних" className="form-block__data-preview">
        <ExtractFormTypePreview />
        <PersonalDataPreview />
        <BirthPlaceAndRegistrationPreview />
        <PurposeAndExtractTypePreview />
        {getValues().extractFormType === ExtractFormType.PAPER && <DeliveryAddressPreview />}
      </FormBlock>

      <ConfirmationOfApplication
        confirmLabelElement={
          <Typography>
            Я підтверджую правильність даних та ознайомлений з{' '}
            <Link isExternal to={ROUTES.PERSONAL_DATA}>
              політикою обробки персональних даних
            </Link>
          </Typography>
        }
      />

      <StepControlPanel prev={prev} next={handleNextStep} disabledNext={!confirmEnteredDataCorrect} />
    </ConditionView>
  );
};
